import Phaser from 'phaser';
import type { GameScene } from './scenes/GameScene';
import type { HudScene } from './scenes/HudScene';
import { BALANCE } from '../config/balance';
import { PLOT_IDLE_ALPHA } from './theme';

// ---------------------------------------------------------------------------
// Hit-test radii (world pixels)
// ---------------------------------------------------------------------------
/** Maximum distance from a plot centre to register a placement tap. */
const PLOT_RADIUS = 40;
/** Maximum distance from a tower centre to register a selection tap. */
const TOWER_RADIUS = 28;

// ---------------------------------------------------------------------------
// Highlight colours
// ---------------------------------------------------------------------------
/** Green tint for free plots in placement mode. */
const FREE_TINT = 0x00ff44;
/** Red tint for occupied plots in placement mode. */
const OCCUPIED_TINT = 0xff4444;

/**
 * InputController
 *
 * Translates pointer events (mouse + touch) into GameCommands and keeps
 * visual feedback (plot highlights, range ring) in sync each frame.
 *
 * Lifecycle:
 *   - Constructed once in GameScene.create(), registered on the scene's
 *     input manager via a single scene-level listener.
 *   - update() must be called from GameScene.update() after syncViews().
 */
export class InputController {
  private readonly scene: GameScene;
  /** Graphics layer for the selected-tower range ring (depth 5). */
  private readonly rangeRing: Phaser.GameObjects.Graphics;

  constructor(scene: GameScene) {
    this.scene = scene;
    this.rangeRing = scene.add.graphics().setDepth(5);

    // Single scene-level pointer listener — avoids per-sprite interactivity
    // which breaks when sprites are created/destroyed dynamically.
    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.onPointerDown(pointer.worldX, pointer.worldY);
    });
  }

  // ---------------------------------------------------------------------------
  // Pointer handler
  // ---------------------------------------------------------------------------

  private onPointerDown(x: number, y: number): void {
    // Block world interaction once the game is over — the result overlay's own
    // Retry / Menu buttons handle pointer events independently.
    if (this.scene.sim.isOver) return;

    // Block world interaction when the tap lands on the HUD overlay.
    // HudScene runs on top via scene.launch; its coordinate space is identical
    // to the logical 1280×720 canvas (no camera scroll on either scene).
    const hud = this.scene.scene.get('HudScene') as HudScene | undefined;
    if (hud && hud.isPointerOverHud(x, y)) return;

    const gs = this.scene;
    const state = gs.sim.state;

    if (gs.pendingTowerType != null) {
      // ── Placement mode ───────────────────────────────────────────────────
      // Find the nearest FREE plot within PLOT_RADIUS of the tap.
      let bestId: number | null = null;
      let bestDist = Infinity;

      for (const plot of gs.level.plots) {
        const dx = plot.pos.x - x;
        const dy = plot.pos.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= PLOT_RADIUS) {
          const occupied = state.towers.some((t) => t.plotId === plot.id);
          if (!occupied && dist < bestDist) {
            bestId = plot.id;
            bestDist = dist;
          }
        }
      }

      if (bestId !== null) {
        const result = gs.sim.dispatch({
          type: 'placeTower',
          towerType: gs.pendingTowerType,
          plotId: bestId,
        });
        if (result.ok) {
          gs.pendingTowerType = null;
          this.clearPlotHighlights();
        }
        // If dispatch failed (e.g. insufficient cash), stay in placement mode.
      } else {
        // Tap missed all free plots — cancel placement mode.
        gs.pendingTowerType = null;
        this.clearPlotHighlights();
      }
    } else {
      // ── Selection mode ───────────────────────────────────────────────────
      // Find the nearest tower within TOWER_RADIUS of the tap.
      let bestId: number | null = null;
      let bestDist = Infinity;

      for (const tower of state.towers) {
        const dx = tower.pos.x - x;
        const dy = tower.pos.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= TOWER_RADIUS && dist < bestDist) {
          bestId = tower.id;
          bestDist = dist;
        }
      }

      gs.selectedTowerId = bestId; // null = deselect
    }
  }

  // ---------------------------------------------------------------------------
  // Per-frame update (called by GameScene.update() after syncViews)
  // ---------------------------------------------------------------------------

  update(): void {
    const gs = this.scene;
    const state = gs.sim.state;

    // ── Plot highlights ──────────────────────────────────────────────────────
    if (gs.pendingTowerType != null) {
      const now = gs.time.now;
      for (let i = 0; i < gs.plotSprites.length; i++) {
        const plot = gs.level.plots[i];
        const sprite = gs.plotSprites[i];
        const occupied = state.towers.some((t) => t.plotId === plot.id);

        if (occupied) {
          sprite.setTint(OCCUPIED_TINT);
          sprite.setAlpha(0.7);
        } else {
          // Gentle alpha pulse on free plots so the player knows where to tap.
          const pulse = 0.75 + 0.25 * Math.sin(now / 200);
          sprite.setTint(FREE_TINT);
          sprite.setAlpha(pulse);
        }
      }
    } else {
      this.clearPlotHighlights();
    }

    // ── Range ring ───────────────────────────────────────────────────────────
    this.rangeRing.clear();

    if (gs.selectedTowerId != null) {
      const tower = state.towers.find((t) => t.id === gs.selectedTowerId);

      if (!tower) {
        // Tower was sold or otherwise removed — deselect.
        gs.selectedTowerId = null;
      } else {
        const pair = BALANCE.towers[tower.type];
        const range = [pair.tier1, pair.tier2, pair.tier3, pair.tier4 ?? pair.tier3][tower.tier - 1]
          .range;
        this.rangeRing.lineStyle(1, 0xffffff, 0.3);
        this.rangeRing.strokeCircle(tower.pos.x, tower.pos.y, range);
        // A Multi-attacker (Samurai Slicer) has a second, OUTER ring for his
        // thrown daggers, drawn dashed-looking in gold so the two never read
        // as one ring.
        const outer = [pair.tier1, pair.tier2, pair.tier3, pair.tier4 ?? pair.tier3][tower.tier - 1]
          .midStrike?.range;
        if (outer) {
          this.rangeRing.lineStyle(1, 0xffd21f, 0.35);
          this.rangeRing.strokeCircle(tower.pos.x, tower.pos.y, outer);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private clearPlotHighlights(): void {
    for (const sprite of this.scene.plotSprites) {
      sprite.clearTint();
      sprite.setAlpha(PLOT_IDLE_ALPHA);
    }
  }
}
